/**
 * DocsPage — the /docs shell and router.
 *
 * One HTML entry, `history.pushState`, four views (Pregled, Splet, KingCAD,
 * Viri in licence — see docsRouter.ts for the URL scheme). The shell (header,
 * sticky tab bar with the search box, footer) renders once; every route
 * change rewrites `<main>` only, so the search input keeps its focus while
 * the catalogue filters underneath it.
 *
 * Two HALVES since 2026-09-28 (Teo: "/si/docs and /cz/docs"): the country is read
 * from the URL once per page load (`parseDocsUrl`), pinned for the path builders
 * (`setDocsCountry`) and the copy (`setDocsLanguage` — Czech chrome on /cz/docs,
 * `<html lang>` follows), and the catalogue is built for that half only. The
 * header's SI · CZ switch is a plain link to the OTHER half's Pregled (Teo: "switching
 * between si/cz should put you on the pregled page for each") — a full load; nothing
 * else crosses.
 *
 * Two sections since 0584: an entry URL (`…/docs/<key>`) belongs to the Web
 * tab or the CAD tab by `entrySection` (a product only KingCAD serves reads
 * under the CAD tab); the KingCAD nav is a collapsible tree whose open folders
 * live in `openFolders` (memory only — a navigation opens the path to the
 * active page, a reload lands on it again). KingCAD LAYERS have no page of
 * their own (Teo 2026-09-16): a folder that holds layers is one page
 * (`…/docs/cad/<slugs…>`, `renderCadFolder`) and `…/docs/<cad key>` is
 * replaced by that page's URL + the layer's section anchor (`canonicalPath`).
 * WEB products have none either since 2026-09-28 (Teo: "1 group is 1 page"):
 * a theme is one page (`…/docs/web/<slug>`, `renderWebPage`) and
 * `…/docs/<web key>` is replaced the same way; `…/docs/katalog` lands on the
 * Web overview (`…/docs/web`).
 *
 * Navigation elements are real links (`<a href data-docs-link>`): middle-click,
 * "copy link" and a no-JS visit all work, and the click delegate turns an
 * unmodified left click into a pushState. The catalogue is fetched once
 * (WikiApiClient memoises); a stale response never renders (requestSeq).
 */

import type { DocsSection } from './docsSection.js';
import type { WikiLayersResponse } from '../../shared/types/layerWiki.js';
import { BRAND } from '../../shared/utils/brand.js';
import { formatLjubljanaTs } from '../../shared/utils/ljubljanaTime.js';
import { appFromReferrer, docsBackLinks, docsMapAppFor, readBackApp, storeBackApp } from './docsBackLinks.js';
import { docsCopy, docsLanguageFor, setDocsLanguage } from './docsCopy.js';
import { beginEdit, cancelEdit, errorCopyFor, initialEditState, patchFromDraft, type EditState } from './docsEdit.js';
import { esc, ICON_SEARCH } from './docsHtml.js';
import { buildCatalogue, cadFolderLabel, cadLayerHref, cadPageForPath, entrySection, webEntryHref, webPageForSlug, type DocsCatalogue } from './docsModel.js';
import {
  DEFAULT_DOCS_COUNTRY,
  DOCS_COUNTRIES,
  docsPath,
  isSameRoute,
  parseDocsUrl,
  setDocsCountry,
  type DocsCountry,
  type DocsRoute,
} from './docsRouter.js';
import { wikiApi, type WikiApi } from './WikiApiClient.js';
import { parseWikiKey } from './wikiKey.js';
import { renderCadFolder, renderCadOverview } from './views/renderCad.js';
import { renderKatalog } from './views/renderKatalog.js';
import { renderPregled } from './views/renderPregled.js';
import { renderViri } from './views/renderViri.js';
import { renderWeb, renderWebPage } from './views/renderWeb.js';

export interface DocsPageOptions {
  /** Injected for tests; defaults to the module singleton. */
  api?: WikiApi;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

interface NavigateOptions {
  replace?: boolean;
  /** Keep the current focus (typing in the search box must not lose it). */
  keepFocus?: boolean;
}

export class DocsPage {
  private readonly root: HTMLElement;
  private readonly api: WikiApi;

  private cat: DocsCatalogue | null = null;
  private loadState: LoadState = 'idle';
  /** Monotonic request token — a stale response never renders. */
  private requestSeq = 0;
  private destroyed = false;

  private route: DocsRoute = { view: 'pregled' };
  /** The /docs half this page load is (2026-09-28): from the URL, fixed until the next full load. */
  private country: DocsCountry = DEFAULT_DOCS_COUNTRY;
  private query = '';
  /** Narrow layout: the nav list is open. */
  private navOpen = false;
  /** KingCAD nav: expanded folder ids (0584). A navigation adds the active entry's path. */
  private readonly openFolders = new Set<string>();

  private mainEl: HTMLElement | null = null;
  private searchEl: HTMLInputElement | null = null;
  private envEl: HTMLElement | null = null;
  /** #875: the app »Zemljevid« / »Začetna stran« lead to (docsBackLinks.ts); null until known. */
  private backApp: string | null = null;

  /** King-admin editing (0569); `isKingAdmin` flips after the /api/auth/me probe. */
  private readonly edit: EditState = initialEditState();
  /** Monotonic token for the history request — a stale response never renders. */
  private historySeq = 0;
  private historyLoading: string | null = null;
  /** The layer whose »Uredi opis« was opened last — a folder page shows only that one's history. */
  private editFocusKey: string | null = null;

  private readonly onClick = (e: MouseEvent): void => this.handleClick(e);
  private readonly onPopState = (): void => this.handlePopState();
  private readonly onInput = (): void => this.handleInput();
  private readonly onSubmit = (e: Event): void => this.handleSubmit(e);
  private readonly onFormInput = (e: Event): void => this.handleFormInput(e);
  private readonly onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.searchEl && this.query) {
      this.searchEl.value = '';
      this.query = '';
      this.render();
    }
  };

  constructor(root: HTMLElement, opts: DocsPageOptions = {}) {
    this.root = root;
    this.api = opts.api ?? wikiApi;
  }

  initialize(): void {
    // The half comes from the URL once per page load; the copy follows it (Czech chrome on
    // /cz/docs) and has to be in place before the shell renders.
    const { country, route } = parseDocsUrl(window.location.pathname);
    this.country = country;
    this.route = route;
    setDocsCountry(country);
    setDocsLanguage(docsLanguageFor(country));
    document.documentElement.lang = docsLanguageFor(country);
    this.backApp = this.initialBackApp();
    this.renderShell();
    this.root.addEventListener('click', this.onClick);
    this.root.addEventListener('submit', this.onSubmit);
    this.root.addEventListener('input', this.onFormInput);
    window.addEventListener('popstate', this.onPopState);
    this.searchEl?.addEventListener('input', this.onInput);
    this.searchEl?.addEventListener('keydown', this.onKeydown);
    // Route changes scroll to the top themselves; Back must not land mid-page.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    void this.load();
    // Independent of the catalogue: the public page renders whatever this says.
    void this.probeAdmin();
    if (!this.backApp) void this.lookupBackApp();
  }

  destroy(): void {
    this.destroyed = true;
    this.requestSeq += 1;
    this.historySeq += 1;
    this.root.removeEventListener('click', this.onClick);
    this.root.removeEventListener('submit', this.onSubmit);
    this.root.removeEventListener('input', this.onFormInput);
    window.removeEventListener('popstate', this.onPopState);
    this.searchEl?.removeEventListener('input', this.onInput);
    this.searchEl?.removeEventListener('keydown', this.onKeydown);
    this.root.innerHTML = '';
    this.mainEl = this.searchEl = this.envEl = null;
  }

  /** Same-document navigation to a /docs path (may carry a `#hash`). */
  navigate(path: string, opts: NavigateOptions = {}): void {
    const url = new URL(path, window.location.origin);
    // In-page links stay in this half (the path builders default to it); the country part is not re-read.
    let route = parseDocsUrl(url.pathname).route;
    let target = url.pathname + url.hash;
    // A KingCAD layer's own URL lands on its folder page, at its section.
    const canon = this.canonicalPath(route);
    if (canon) {
      target = canon;
      route = parseDocsUrl(new URL(canon, window.location.origin).pathname).route;
    }
    const here = window.location.pathname + window.location.hash;
    if (target !== here) {
      if (opts.replace) window.history.replaceState({}, '', target);
      else window.history.pushState({}, '', target);
    }
    this.route = route;
    this.openPathToActive();
    this.render();
    const hashAt = target.indexOf('#');
    this.afterNavigate(hashAt >= 0 ? target.slice(hashAt) : '', opts.keepFocus === true);
  }

  /**
   * The URL an entry route is replaced by, or null when it stands: `/docs/katalog`
   * → the Web overview; `/docs/<key>` of a WEB product → its theme page + section
   * anchor; of a KingCAD LAYER the tree places → its folder page + section anchor.
   * A product only KingCAD serves, a layer the tree does not place, and every
   * route before the catalogue is in keep their URL.
   */
  private canonicalPath(route: DocsRoute): string | null {
    if (route.view !== 'katalog' || !this.cat) return null;
    if (route.key === null) return docsPath({ view: 'web' });
    const entry = this.cat.byKey.get(route.key);
    if (!entry) return null;
    if (entry.cad) return cadLayerHref(this.cat, route.key);
    return webEntryHref(this.cat, route.key);
  }

  /**
   * Which section the current route belongs to: the CAD tab (the overview, a folder
   * page, or a product only KingCAD serves), or the web catalogue.
   */
  private sectionOf(route: DocsRoute): DocsSection {
    if (route.view === 'cad' || route.view === 'cadfolder') return 'cad';
    if (route.view === 'katalog' && route.key !== null && this.cat) return entrySection(this.cat, route.key);
    return 'web';
  }

  /** The active page's (or CAD entry's) folders open when navigated to (never closes what the user closed). */
  private openPathToActive(): void {
    const r = this.route;
    if (r.view === 'cadfolder') {
      const page = this.cat ? cadPageForPath(this.cat, r.path) : null;
      for (const id of page?.folderIds ?? []) this.openFolders.add(id);
      return;
    }
    if (r.view !== 'katalog' || r.key === null) return;
    const place = this.cat?.cadIndex.get(r.key);
    if (!place) return;
    for (const id of place.folderIds) this.openFolders.add(id);
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  private async load(): Promise<void> {
    const seq = ++this.requestSeq;
    this.loadState = 'loading';
    this.render();
    let res: WikiLayersResponse;
    try {
      res = await this.api.fetchWikiLayers();
    } catch (err) {
      if (seq !== this.requestSeq || this.destroyed) return;
      console.error('[DocsPage] catalogue load failed', err);
      this.loadState = 'error';
      this.render();
      return;
    }
    if (seq !== this.requestSeq || this.destroyed) return;
    this.applyResponse(res);
  }

  /**
   * Re-fetch after a text edit, in place: no »Nalagam …« flash, no
   * afterNavigate (that scrolls to the top and focuses the h1). A failed
   * refetch keeps the catalogue on screen — the edit itself already committed.
   */
  private async refresh(): Promise<void> {
    const seq = ++this.requestSeq;
    let res: WikiLayersResponse;
    try {
      res = await this.api.fetchWikiLayers();
    } catch (err) {
      if (seq !== this.requestSeq || this.destroyed) return;
      console.error('[DocsPage] catalogue refresh failed', err);
      return;
    }
    if (seq !== this.requestSeq || this.destroyed) return;
    this.cat = buildCatalogue(res, this.country);
    this.loadState = 'ready';
    this.render();
  }

  /** #875: the app the reader came from (referrer, remembered per tab), or null. */
  private initialBackApp(): string | null {
    const fromReferrer = appFromReferrer(document.referrer, window.location.origin);
    if (fromReferrer) {
      storeBackApp(fromReferrer);
      return fromReferrer;
    }
    return readBackApp();
  }

  /** #875: no app from the reader's path — the half's own map. Not remembered: it is no path. */
  private async lookupBackApp(): Promise<void> {
    const apps = (await this.api.fetchApps?.()) ?? [];
    if (this.destroyed || this.backApp) return;
    const appId = docsMapAppFor(apps, this.country);
    if (!appId) return;
    this.backApp = appId;
    const nav = this.root.querySelector<HTMLElement>('[data-docs-applinks]');
    if (!nav) return;
    const links = docsBackLinks(appId);
    nav.querySelector('[data-docs-applink="map"]')?.setAttribute('href', links.mapHref);
    nav.querySelector('[data-docs-applink="home"]')?.setAttribute('href', links.homeHref);
    nav.hidden = false;
  }

  private async probeAdmin(): Promise<void> {
    const me = await this.api.fetchMe();
    if (this.destroyed || !me.isKingAdmin) return;
    this.edit.isKingAdmin = true;
    if (this.loadState === 'ready') this.render();
  }

  /** The audit rows of one entry; render() asks for them once per key. */
  private async loadHistory(key: string): Promise<void> {
    if (this.historyLoading === key) return;
    this.historyLoading = key;
    const seq = ++this.historySeq;
    try {
      const res = await this.api.fetchHistory(key);
      if (seq !== this.historySeq || this.destroyed) return;
      this.edit.history = { key, rows: res.rows, failed: false };
    } catch (err) {
      if (seq !== this.historySeq || this.destroyed) return;
      console.error('[DocsPage] history load failed', err);
      this.edit.history = { key, rows: [], failed: true };
    } finally {
      if (this.historyLoading === key) this.historyLoading = null;
    }
    this.render();
  }

  private applyResponse(res: WikiLayersResponse): void {
    this.cat = buildCatalogue(res, this.country);
    this.loadState = 'ready';
    if (this.envEl) {
      this.envEl.textContent =
        res.env === 'prod' ? docsCopy.header.lang : `${docsCopy.header.testBadge} · ${docsCopy.header.lang}`;
    }
    // The /docs/katalog alias, a web product's and a KingCAD layer's URL resolve to their page, URL replaced.
    const canon = this.canonicalPath(this.route);
    if (canon) {
      this.navigate(canon, { replace: true });
      return;
    }
    this.openPathToActive();
    this.render();
    this.afterNavigate(window.location.hash, false);
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  private renderShell(): void {
    const c = docsCopy;
    const [brandA, brandB] = BRAND.name.split('.');
    const wordmark = `${esc(brandA)}<span class="docs-wordmark-dot">.</span>${esc(brandB ?? '')}`;
    // #875: the way back to the app — hidden until the app is known (a full navigation, no data-docs-link)
    const back = this.backApp ? docsBackLinks(this.backApp) : { mapHref: '/map', homeHref: '/d' };
    this.root.innerHTML = `
      <div class="docs-shell">
        <header class="docs-header">
          <a class="docs-brand" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link aria-label="${esc(c.header.homeLabel)}">
            <span class="docs-wordmark">${wordmark}</span>
            <span class="docs-brand-suffix">${esc(c.header.suffix)}</span>
          </a>
          <nav class="docs-applinks" aria-label="${esc(c.header.appLinks)}" data-docs-applinks${this.backApp ? '' : ' hidden'}>
            <a class="docs-applink" data-docs-applink="map" href="${esc(back.mapHref)}">${esc(c.header.mapLink)}</a>
            <a class="docs-applink" data-docs-applink="home" href="${esc(back.homeHref)}">${esc(c.header.homeLink)}</a>
          </nav>
          <span class="docs-header-spacer"></span>
          <nav class="docs-countries" aria-label="${esc(c.header.countries)}">
            ${DOCS_COUNTRIES.map(
              (cc) =>
                `<a class="docs-country" data-docs-country="${cc}" href="${esc(docsPath({ view: 'pregled' }, cc))}" hreflang="${docsLanguageFor(cc)}" title="${esc(c.header.countryNames[cc])}"${cc === this.country ? ' aria-current="page"' : ''}>${cc.toUpperCase()}</a>`
            ).join('')}
          </nav>
          <span class="docs-header-meta" data-docs-env>${esc(c.header.lang)}</span>
          <a class="docs-header-link" href="${esc(BRAND.siteUrl)}" rel="noopener">${esc(c.header.siteLink)}</a>
        </header>
        <div class="docs-tabbar">
          <nav class="docs-tabs" aria-label="${esc(c.tabsLabel)}">
            <a class="docs-tab" data-tab="pregled" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(c.tabs.pregled)}</a>
            <a class="docs-tab" data-tab="katalog" href="${esc(docsPath({ view: 'web' }))}" data-docs-link>${esc(c.tabs.katalog)}</a>
            <a class="docs-tab" data-tab="cad" href="${esc(docsPath({ view: 'cad' }))}" data-docs-link>${esc(c.tabs.cad)}</a>
            <a class="docs-tab" data-tab="viri" href="${esc(docsPath({ view: 'viri' }))}" data-docs-link>${esc(c.tabs.viri)}</a>
          </nav>
          <div class="docs-search">
            <span class="docs-search-icon" aria-hidden="true">${ICON_SEARCH}</span>
            <input type="search" class="docs-search-input" aria-label="${esc(c.searchLabel)}" placeholder="${esc(c.searchPlaceholder)}" autocomplete="off" spellcheck="false">
          </div>
        </div>
        <main id="docs-main" class="docs-main"></main>
        <footer class="docs-footer">
          <span class="docs-wordmark docs-wordmark--footer">${wordmark}</span>
          <a href="mailto:${esc(BRAND.supportEmail)}">${esc(BRAND.supportEmail)}</a>
        </footer>
      </div>`;
    this.mainEl = this.root.querySelector<HTMLElement>('#docs-main');
    this.searchEl = this.root.querySelector<HTMLInputElement>('.docs-search-input');
    this.envEl = this.root.querySelector<HTMLElement>('[data-docs-env]');
  }

  private render(): void {
    const main = this.mainEl;
    if (!main) return;

    if (this.loadState === 'idle' || this.loadState === 'loading') {
      main.setAttribute('aria-busy', 'true');
      main.innerHTML = `<p class="docs-state" role="status">${esc(docsCopy.loading)}</p>`;
      document.title = docsCopy.titles.base;
      this.syncTabs();
      return;
    }
    main.removeAttribute('aria-busy');

    const cat = this.cat;
    if (this.loadState === 'error' || !cat) {
      main.innerHTML = `
        <div class="docs-state" role="alert">
          <p>${esc(docsCopy.error)}</p>
          <button type="button" class="docs-btn-primary" data-docs-retry>${esc(docsCopy.retry)}</button>
        </div>`;
      document.title = docsCopy.titles.base;
      this.syncTabs();
      return;
    }
    if (cat.all.length === 0) {
      main.innerHTML = `<p class="docs-state" role="status">${esc(docsCopy.noEntries)}</p>`;
      document.title = docsCopy.titles.base;
      this.syncTabs();
      return;
    }

    const first = cat.entries[0]?.key ?? null;
    switch (this.route.view) {
      case 'pregled':
        main.innerHTML = renderPregled(cat);
        document.title = docsCopy.titles.base;
        break;
      case 'viri':
        main.innerHTML = renderViri(cat);
        document.title = docsCopy.titles.viri;
        break;
      case 'web': {
        const navScroll = main.querySelector<HTMLElement>('.docs-nav')?.scrollTop ?? 0;
        main.innerHTML = renderWeb(cat, { query: this.query, navOpen: this.navOpen });
        const navAfter = main.querySelector<HTMLElement>('.docs-nav');
        if (navAfter && navScroll > 0) navAfter.scrollTop = navScroll;
        document.title = docsCopy.titles.web;
        break;
      }
      case 'webpage': {
        const page = webPageForSlug(cat, this.route.slug);
        const navScroll = main.querySelector<HTMLElement>('.docs-nav')?.scrollTop ?? 0;
        main.innerHTML = renderWebPage(cat, page, { query: this.query, navOpen: this.navOpen, edit: this.edit });
        const navAfter = main.querySelector<HTMLElement>('.docs-nav');
        if (navAfter && navScroll > 0) navAfter.scrollTop = navScroll;
        document.title = page ? docsCopy.titles.webPage(page.theme) : docsCopy.titles.notFound;
        // An admin sees the edit history of the entry whose editor was opened last on this page.
        const focus = this.editFocusKey;
        if (page && focus && this.edit.isKingAdmin && page.entries.some((e) => e.key === focus) && this.edit.history?.key !== focus) {
          void this.loadHistory(focus);
        }
        break;
      }
      case 'cad': {
        const navScroll = main.querySelector<HTMLElement>('.docs-nav')?.scrollTop ?? 0;
        main.innerHTML = renderCadOverview(cat, { query: this.query, navOpen: this.navOpen, openFolders: this.openFolders });
        const navAfter = main.querySelector<HTMLElement>('.docs-nav');
        if (navAfter && navScroll > 0) navAfter.scrollTop = navScroll;
        document.title = docsCopy.titles.cad;
        break;
      }
      case 'cadfolder': {
        const page = cadPageForPath(cat, this.route.path);
        const navScroll = main.querySelector<HTMLElement>('.docs-nav')?.scrollTop ?? 0;
        main.innerHTML = renderCadFolder(cat, page, { query: this.query, navOpen: this.navOpen, openFolders: this.openFolders, edit: this.edit });
        const navAfter = main.querySelector<HTMLElement>('.docs-nav');
        if (navAfter && navScroll > 0) navAfter.scrollTop = navScroll;
        document.title = page ? docsCopy.titles.cadFolder(cadFolderLabel(cat, page.folder).label) : docsCopy.titles.notFound;
        // An admin sees the edit history of the layer whose editor was opened last on this page.
        const focus = this.editFocusKey;
        if (page && focus && this.edit.isKingAdmin && page.layers.some((l) => l.entryKey === focus) && this.edit.history?.key !== focus) {
          void this.loadHistory(focus);
        }
        break;
      }
      case 'katalog': {
        const key = this.route.key ?? first;
        const entry = key !== null && parseWikiKey(key) === key ? (cat.byKey.get(key) ?? null) : null;
        const section: DocsSection = entry ? entrySection(cat, entry.key) : 'web';
        // The nav list is its own scroller; keep its position across re-renders.
        const navBefore = main.querySelector<HTMLElement>('.docs-nav');
        const navScroll = navBefore?.scrollTop ?? 0;
        main.innerHTML = renderKatalog(cat, { key, query: this.query, navOpen: this.navOpen, section, openFolders: this.openFolders, edit: this.edit });
        const navAfter = main.querySelector<HTMLElement>('.docs-nav');
        if (navAfter && navScroll > 0) navAfter.scrollTop = navScroll;
        document.title = entry ? docsCopy.titles.entry(entry.title) : docsCopy.titles.notFound;
        // An admin sees this entry's edit history; fetched once per key, re-rendered when it lands.
        if (entry && this.edit.isKingAdmin && this.edit.history?.key !== entry.key) void this.loadHistory(entry.key);
        break;
      }
    }
    this.syncTabs();
  }

  /**
   * `aria-current` on the active tab — the CAD overview, a folder page and a product
   * only KingCAD serves light the KingCAD tab; the Web overview, a theme page and an
   * unknown key the Web tab.
   */
  private syncTabs(): void {
    this.syncLayout();
    const activeTab = this.sectionOf(this.route) === 'cad' ? 'cad' : this.route.view === 'pregled' || this.route.view === 'viri' ? this.route.view : 'katalog';
    for (const tab of this.root.querySelectorAll<HTMLAnchorElement>('.docs-tab[data-tab]')) {
      const id = tab.dataset['tab'];
      if (id === activeTab) tab.setAttribute('aria-current', 'page');
      else tab.removeAttribute('aria-current');
    }
  }

  /**
   * `data-docs-layout` on the shell: `nav` (theme list), `tree` (KingCAD tree) or `none`.
   * On desktop the sidebar is a FIXED column beside the page, so the footer — which sits
   * outside <main> — has to start to its right; the CSS reads this attribute for that.
   */
  private syncLayout(): void {
    const shell = this.root.querySelector<HTMLElement>('.docs-shell');
    if (!shell) return;
    const nav = this.mainEl?.querySelector('.docs-nav');
    shell.dataset['docsLayout'] = !nav ? 'none' : nav.classList.contains('docs-nav--tree') ? 'tree' : 'nav';
  }

  /** Scroll to the hash target, or to the top with focus on the view's heading. */
  private afterNavigate(hash: string, keepFocus: boolean): void {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView?.();
        // The web fonts land after the first paint and push the target down;
        // scroll once more when they are in (a no-op when already loaded).
        document.fonts?.ready
          .then(() => {
            if (!this.destroyed && el.isConnected) el.scrollIntoView?.();
          })
          .catch(() => undefined);
        return;
      }
    }
    window.scrollTo?.(0, 0);
    if (keepFocus) return;
    this.mainEl?.querySelector<HTMLElement>('h1[tabindex="-1"]')?.focus({ preventScroll: true });
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  private handleClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const toggle = target.closest<HTMLElement>('[data-docs-nav-toggle]');
    if (toggle) {
      this.navOpen = !this.navOpen;
      toggle.setAttribute('aria-expanded', this.navOpen ? 'true' : 'false');
      const list = this.root.querySelector<HTMLElement>('#docs-nav-list');
      if (list) list.hidden = !this.navOpen;
      return;
    }
    if (target.closest('[data-docs-retry]')) {
      this.api.invalidate();
      void this.load();
      return;
    }
    if (target.closest('[data-docs-clear]')) {
      if (this.searchEl) this.searchEl.value = '';
      this.query = '';
      this.render();
      this.searchEl?.focus();
      return;
    }
    // KingCAD tree: a folder's disclosure button (0584). Full re-render, focus back on the button.
    const folderBtn = target.closest<HTMLElement>('[data-docs-folder-toggle]');
    if (folderBtn) {
      const id = folderBtn.dataset['docsFolderToggle'];
      if (!id) return;
      if (this.openFolders.has(id)) this.openFolders.delete(id);
      else this.openFolders.add(id);
      this.render();
      this.focusIn(`[data-docs-folder-toggle="${id}"]`);
      return;
    }

    // King-admin editing (0569)
    const editBtn = target.closest<HTMLElement>('[data-docs-edit]');
    if (editBtn) {
      const key = editBtn.dataset['docsEdit'];
      const entry = key ? this.cat?.byKey.get(key) : undefined;
      if (!entry || !this.edit.isKingAdmin) return;
      beginEdit(this.edit, entry);
      this.editFocusKey = entry.key;
      this.render();
      this.focusIn('#docs-edit-sl');
      return;
    }
    if (target.closest('[data-docs-edit-cancel]')) {
      cancelEdit(this.edit);
      this.render();
      this.focusIn('[data-docs-edit]');
      return;
    }
    const restoreBtn = target.closest<HTMLButtonElement>('[data-docs-restore]');
    if (restoreBtn) {
      if (restoreBtn.disabled || this.edit.busy) return;
      const auditId = restoreBtn.dataset['docsRestore'];
      const history = this.edit.history;
      const row = auditId ? history?.rows.find((r) => r.id === auditId) : undefined;
      if (!row || !history) return;
      if (!window.confirm(docsCopy.edit.restoreConfirm(formatLjubljanaTs(row.changedAt, 'datetime')))) return;
      void this.restore(history.key, row.id);
      return;
    }

    const link = target.closest<HTMLAnchorElement>('a[data-docs-link]');
    if (!link) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const href = link.getAttribute('href');
    if (!href) return;
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    this.navigate(url.pathname + url.hash);
  }

  private handlePopState(): void {
    const route = parseDocsUrl(window.location.pathname).route;
    // A hash-only change (the »Na tej strani« rail) is the browser's to scroll.
    if (isSameRoute(route, this.route)) return;
    const canon = this.canonicalPath(route);
    if (canon) {
      this.navigate(canon, { replace: true });
      return;
    }
    this.route = route;
    this.openPathToActive();
    this.render();
    this.afterNavigate(window.location.hash, false);
  }

  /**
   * Typing filters the section under the cursor: the KingCAD tree and the web
   * page list stay where they are (pages drop, matching layers list flat);
   * Pregled and Viri jump to the Web overview once and filter there.
   */
  private handleInput(): void {
    this.query = this.searchEl?.value ?? '';
    if (this.loadState !== 'ready' || !this.cat) return;
    if (this.query.trim() !== '') this.navOpen = true;
    if (this.route.view === 'pregled' || this.route.view === 'viri') {
      this.navigate(docsPath({ view: 'web' }), { keepFocus: true });
      return;
    }
    this.render();
  }

  // ---------------------------------------------------------------------------
  // King-admin editing (0569)
  // ---------------------------------------------------------------------------

  /** Mirror every keystroke into the draft, so a re-render (the search box) keeps it. */
  private handleFormInput(e: Event): void {
    const field = e.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!field || !field.closest('form[data-docs-edit-form]')) return;
    const d = this.edit.draft;
    if (field.name === 'descriptionSl') d.sl = field.value;
    else if (field.name === 'descriptionCs') d.cs = field.value;
    else if (field.name === 'note') d.note = field.value;
    // Typing is the user's correction: clear the message without a re-render (the caret stays).
    if (this.edit.error) {
      this.edit.error = null;
      this.mainEl?.querySelector('.docs-edit-error')?.remove();
      this.mainEl?.querySelector('#docs-edit-sl')?.removeAttribute('aria-invalid');
    }
  }

  private handleSubmit(e: Event): void {
    const form = (e.target as HTMLElement | null)?.closest<HTMLFormElement>('form[data-docs-edit-form]');
    if (!form) return;
    e.preventDefault();
    const key = form.dataset['docsEditForm'];
    if (!key || this.edit.busy || this.edit.editingKey !== key) return;
    if (this.edit.draft.sl.trim() === '') {
      this.edit.error = docsCopy.edit.errors.emptySl;
      this.render();
      this.focusIn('#docs-edit-sl');
      return;
    }
    void this.save(key);
  }

  private async save(key: string): Promise<void> {
    this.edit.busy = true;
    this.edit.error = null;
    this.render();
    try {
      await this.api.patchEntryText(key, patchFromDraft(this.edit.draft));
    } catch (err) {
      if (this.destroyed) return;
      console.error('[DocsPage] text edit failed', err);
      this.edit.busy = false;
      this.edit.error = errorCopyFor(err);
      this.render();
      this.focusIn('#docs-edit-sl');
      return;
    }
    if (this.destroyed) return;
    this.edit.busy = false;
    cancelEdit(this.edit);
    this.edit.history = null; // the new audit row
    this.api.invalidate();
    await this.refresh();
    if (this.destroyed) return;
    // Back on the edited layer's button — on a folder page there is one per layer.
    this.focusIn(`[data-docs-edit="${key}"]`);
    const btn = this.mainEl?.querySelector(`[data-docs-edit="${key}"]`);
    (btn?.closest('section') ?? document.getElementById('sec-opis'))?.scrollIntoView?.({ block: 'nearest' });
  }

  private async restore(key: string, auditId: string): Promise<void> {
    this.edit.busy = true;
    this.edit.error = null;
    this.render();
    try {
      await this.api.restoreEntryText(key, { auditId });
    } catch (err) {
      if (this.destroyed) return;
      console.error('[DocsPage] text restore failed', err);
      this.edit.busy = false;
      this.edit.error = errorCopyFor(err);
      this.render();
      return;
    }
    if (this.destroyed) return;
    this.edit.busy = false;
    this.edit.history = null;
    this.api.invalidate();
    await this.refresh();
    if (this.destroyed) return;
    document.getElementById('sec-opis')?.scrollIntoView?.({ block: 'nearest' });
  }

  private focusIn(selector: string): void {
    this.mainEl?.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true });
  }
}
